import { useEffect, useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCirclePlay } from "@fortawesome/free-solid-svg-icons";
import "./date-range-slider.scss";
import { getTimeAsPerBlackoutHours } from "../../utils";

type Props = {
    handleDateChange: (date: string) => void;
    isFromMobile: boolean;
    initDate: string;
    blackoutHours: string,
    blackoutDays: string,
    practiceTimeZone: string,
    isFromQuestionnaire: boolean,
    classNameProps?:string,
    fromAvailabilityWidget?: boolean,
};

export default function DateNavigator({
    handleDateChange,
    isFromMobile,
    initDate,
    blackoutHours,
    blackoutDays,
    practiceTimeZone,
    isFromQuestionnaire,
    classNameProps = 'date-navigator',
    fromAvailabilityWidget = false,
}: Props) {
    const [initCompleted, setInitCompleted] = useState<boolean>();
    const daysToBeProcessed = isFromMobile ? 3 : 5;
    const [currentDate, setCurrentDate] = useState<moment.Moment>();
    const [navigationStartDate, setNavigationStartDate] =
        useState<moment.Moment>();
    const [navigationEndDate, setNavigationEndDate] = useState<moment.Moment>();

    useEffect(() => {
        if (!initCompleted && currentDate) {
            setInitCompleted(true);
        }
    }, [currentDate, initCompleted]);

    useEffect(() => {
        if (initDate) {
            setNavigationStartDate(moment(initDate));
            setNavigationEndDate(moment(initDate).add(20, "days"));
            setCurrentDate(moment(initDate));
        }
    }, []);

    useEffect(() => {
        if (initDate) {
            setCurrentDate(moment(initDate));
        }
    }, [initDate]);

    const handlePrevSet = () => {
        const newDate = moment(currentDate).subtract(daysToBeProcessed, "days");

        if (navigationStartDate && newDate > navigationStartDate) {
            onDateChange(newDate);
        } else {
            const startDateAndTime = getTimeAsPerBlackoutHours(
                new Date(),
                blackoutHours,
                blackoutDays,
                !isFromQuestionnaire ? practiceTimeZone : '',
            );

            handleDateChange(startDateAndTime);
        }
    };

    const handleNextSet = () => {
        const newDate = moment(currentDate).add(daysToBeProcessed, "days");

        onDateChange(newDate);
    };

    const onDateChange = (date: moment.Moment, sendInitDate?: boolean) => {
        const dateonly = date.format("YYYY-MM-DD");
        const dateObj = moment(dateonly);

        const dateTime = !isFromQuestionnaire && practiceTimeZone ?
            moment.tz(dateObj.format("YYYY-MM-DDTHH:mm"), practiceTimeZone) : dateObj;

        // This is to make date always start from 12:00
        // So that weird time gap doesn't happen
        dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

        if (sendInitDate) {
            handleDateChange(date?.format("YYYY-MM-DDTHH:mm:ss.SSSZ"));
        } else {
            handleDateChange(dateTime.format("YYYY-MM-DDTHH:mm:ss.SSSZ"));
        }
    };

    const isMinScroll = () => {
        if (currentDate) {
            if(fromAvailabilityWidget) {
                return (
                    currentDate.isSame(navigationStartDate, "day") ||
                    currentDate.isBefore(navigationStartDate, "day")
                );
            } else {
                return (
                    currentDate.isSame(navigationStartDate) ||
                    currentDate.isBefore(navigationStartDate)
                );
            }
        } else {
            return false;
        }
    };

    const isMaxScroll = () => {
        if (currentDate) {
            const lastDate = moment(currentDate).add(daysToBeProcessed, "days");

            return lastDate.isAfter(navigationEndDate);
        } else {
            return false;
        }
    };

    const renderDays = () => {
        const days = [];

        for (let i = 0; i < daysToBeProcessed; i++) {
            const day = moment(currentDate).add(i, "days");

            days.push(
                <div key={i} className="day">
                    <span className="day-name">{day.format("ddd")}</span>
                    {day.format("MM/DD")}
                </div>
            );
        }

        return days;
    };

    return (
        <>
            {initCompleted && currentDate && (
                <div className={classNameProps}>
                    <FontAwesomeIcon
                        className={`arrow arrowLeft ${
                            isMinScroll() ? "arrow-disabled" : ""
                        }`}
                        icon={faCirclePlay}
                        onClick={() => {
                            if (!isMinScroll()) {
                                handlePrevSet();
                            }
                        }}
                        rotation={180}
                        color={
                            currentDate?.isSame(navigationStartDate, "day")
                                ? "#3543546e"
                                : ""
                        }
                    />
                    <div className="days-wrapper">{renderDays()}</div>
                    <FontAwesomeIcon
                        className={`arrow arrowRight ${
                            isMaxScroll() ? "arrow-disabled" : ""
                        }`}
                        icon={faCirclePlay}
                        onClick={() => {
                            if (!isMaxScroll()) {
                                handleNextSet();
                            }
                        }}
                    />
                </div>
            )}
        </>
    );
}
